import { useCallback, useEffect, useState } from 'react';

import { ApiError, listMyTasks, updateTask, type MyTask, type TaskStatus } from '../api.js';
import { formatDate } from '../labels.js';
import { navigate } from '../router.js';

/**
 * Everything assigned to one person, across every project.
 *
 * The answer to "what am I meant to do today" without opening each project in
 * turn — which is how anyone on site actually works.
 *
 * Grouped by DUE-ness rather than by project, because the question is about
 * time. Which project a task belongs to is a label on the row, not the
 * organising principle.
 */

const STATUS_LABELS: Readonly<Record<TaskStatus, string>> = {
  todo: 'To do',
  in_progress: 'In progress',
  blocked: 'Blocked',
  done: 'Done',
  cancelled: 'Cancelled',
};

const MOVE_LABELS: Readonly<Record<TaskStatus, string>> = {
  todo: 'Back to to-do',
  in_progress: 'Start',
  blocked: 'Block',
  done: 'Done',
  cancelled: 'Cancel',
};

type State =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly tasks: readonly MyTask[] }
  | { readonly status: 'failed'; readonly message: string };

export function MyWork(): JSX.Element {
  const [state, setState] = useState<State>({ status: 'loading' });
  const [showFinished, setShowFinished] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (finished: boolean) => {
    try {
      setState({ status: 'ready', tasks: await listMyTasks(finished) });
    } catch {
      setState({ status: 'failed', message: 'Could not load your work.' });
    }
  }, []);

  useEffect(() => {
    void load(showFinished);
  }, [load, showFinished]);

  async function move(task: MyTask, to: TaskStatus): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await updateTask(task.project_id, task.id, { status: to });
      await load(showFinished);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  if (state.status === 'loading') {
    return (
      <section>
        <div className="section-head">
          <h2>My work</h2>
        </div>
        <p aria-live="polite">Loading…</p>
      </section>
    );
  }

  if (state.status === 'failed') {
    return (
      <section>
        <div className="section-head">
          <h2>My work</h2>
        </div>
        <div className="alert" role="alert">
          <span>{state.message}</span>
        </div>
      </section>
    );
  }

  const overdue = state.tasks.filter((t) => t.overdue);
  const rest = state.tasks.filter((t) => !t.overdue);

  return (
    <section>
      <div className="section-head">
        <h2>My work</h2>
        <button
          type="button"
          className={showFinished ? 'chip-button current' : 'chip-button'}
          aria-pressed={showFinished}
          onClick={() => setShowFinished((v) => !v)}
        >
          Include finished
        </button>
      </div>

      {error && (
        <div className="alert" role="alert">
          <span>{error}</span>
        </div>
      )}

      {state.tasks.length === 0 ? (
        <div className="empty">
          <p>
            <strong>Nothing assigned to you.</strong>
          </p>
          <p>Work shows up here when someone puts your name on it.</p>
        </div>
      ) : (
        <>
          {overdue.length > 0 && (
            <>
              {/* Overdue first and under its own heading — the whole reason
                  someone opens this screen is to find what has slipped. */}
              <h3 className="sub-head">Overdue ({overdue.length})</h3>
              <TaskGroup tasks={overdue} busy={busy} onMove={move} />
            </>
          )}

          {rest.length > 0 && (
            <>
              <h3 className="sub-head">
                {overdue.length > 0 ? 'Everything else' : `Assigned to you (${rest.length})`}
              </h3>
              <TaskGroup tasks={rest} busy={busy} onMove={move} />
            </>
          )}
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------

interface GroupProps {
  readonly tasks: readonly MyTask[];
  readonly busy: boolean;
  readonly onMove: (task: MyTask, to: TaskStatus) => void | Promise<void>;
}

function TaskGroup({ tasks, busy, onMove }: GroupProps): JSX.Element {
  return (
    <ul className="task-list">
      {tasks.map((task) => (
        <li key={task.id} className={`task-row task-${task.status}`}>
          <div className="task-main">
            <span className="task-title">{task.title}</span>
            <span className="task-meta">
              {/* A real link: this is the one screen where you often need to
                  jump to the project the work belongs to. */}
              <a
                href={`/projects/${encodeURIComponent(task.project_id)}`}
                onClick={(event) => {
                  if (event.metaKey || event.ctrlKey || event.shiftKey) return;
                  event.preventDefault();
                  navigate(`/projects/${encodeURIComponent(task.project_id)}`);
                }}
              >
                {task.project_code} {task.project_name}
              </a>
              <span>· {STATUS_LABELS[task.status]}</span>
              {task.location_path && <span>· {task.location_path}</span>}
              {task.due_date && (
                <span className={task.overdue ? 'task-overdue' : undefined}>
                  · {task.overdue ? 'Was due' : 'Due'} {formatDate(task.due_date)}
                </span>
              )}
            </span>
            {task.description && <span className="task-description">{task.description}</span>}
          </div>

          {/* Why your own work is stopped is the most useful thing this screen
              can tell you, so it travels with the row here too. */}
          {task.blockers.length > 0 && (
            <ul className="blocker-list">
              {task.blockers.map((blocker) => (
                <li key={blocker.id} className={`blocker sev-${blocker.severity}`}>
                  <span className="blocker-why">{blocker.description}</span>
                  <span className="blocker-age">
                    {blocker.days_open === 0
                      ? 'Raised today'
                      : `Open ${blocker.days_open} day${blocker.days_open === 1 ? '' : 's'}`}
                  </span>
                </li>
              ))}
            </ul>
          )}

          <span className="row-actions">
            {task.allowed_next
              // Cancelling your own work from a to-do list is too easy to do by
              // accident. It stays on the project board, where the context is.
              .filter((to) => to !== 'cancelled')
              .map((to) => (
                <button
                  key={to}
                  type="button"
                  className="link-button"
                  disabled={busy}
                  onClick={() => void onMove(task, to)}
                >
                  {MOVE_LABELS[to]}
                  <span className="visually-hidden"> {task.title}</span>
                </button>
              ))}
          </span>
        </li>
      ))}
    </ul>
  );
}
